<template>
    <div class="w-full px-12 mt-48 xs:w-1/2 sm:w-1/3">
        <!--    CARD START-->
        <div>
            <div class="flex items-center">
                <div class="flex items-center justify-center flex-shrink-0 w-48 h-48 bg-yellow-200 rounded-full">
                    <div class="flex-shrink-0 block w-24 h-24 fill-current text-primary">
                        <IconNotesSVG class="w-24 h-24"/>
                    </div>
                </div>

                <span class="ml-12 font-normal title-lg text-primary">{{ $t('info.my_info') }}</span>
            </div>

            <span v-if="customerInfo.firstname && customerInfo.lastname"
                  class="mt-16 self-label">Nimi</span>
            <span v-if="customerInfo.firstname && customerInfo.lastname"
                  class="text-base font-semibold sm:text-md">{{ customerInfo.firstname }} {{ customerInfo.lastname }}</span>

            <span v-if="customerInfo.personal_code" class="mt-16 self-label">{{ $t('info.identity_code') }}</span>
            <span v-if="customerInfo.personal_code"
                  class="text-base font-semibold sm:text-md">{{ customerInfo.personal_code }}</span>

            <span v-if="customerInfo.personal_code" class="mt-16 self-label">{{ $t('info.dob') }}</span>
            <span v-if="customerInfo.personal_code"
                  class="text-base font-semibold sm:text-md">{{ birthDate }}</span>

            <span v-if="customerInfo.personal_code" class="mt-16 self-label">{{ $t('info.gender') }}</span>
            <span v-if="customerInfo.personal_code"
                  class="text-base font-semibold sm:text-md">{{ translatedGender }}</span>

            <button data-open-modal="andmed_modal" class="mt-16 self-btn-plain">
                <span class="w-16 ml-0 mr-4 self-btn-plain__icon">
                    <IconPencilSVG/>
                </span>
                <span class="self-btn-plain__text">{{ $t('info.modify') }}</span>
            </button>
        </div>
        <!--    CARD END-->

        <!--     MODAL START-->
        <div class="micromodal" id="andmed_modal" aria-hidden="true">
            <div class="px-10 micromodal__overlay" tabindex="-1">
                <div class="micromodal__container" role="dialog" aria-modal="true" aria-labelledby="modal-title">
                    <header class="micromodal__header">
                        <h2 class="pb-24 pr-48 title-2xl text-primary">{{ $t('info.edit_my_info') }}</h2>

                        <button @click="cancelChanges" class="micromodal__close" aria-label="Close modal"
                                data-micromodal-close>
                            <span class="flex-shrink-0 block w-20 h-20 text-black pointer-events-none fill-current">
                                <IconXSVG/>
                            </span>
                        </button>
                    </header>
                    <div class="micromodal__content">

                        <div class="flex self-form-wrap">
                            <div class="w-full self-form-wrap-item">
                                <label class="e5-form-label" for="name">{{ $t('info.name') }}</label>
                                <div class="relative">
                                    <input type="text" id="name" placeholder="" name="name"
                                           :value="customerInfo.firstname + ' ' + customerInfo.lastname" disabled
                                           class="e5-input">
                                </div>
                            </div>

                            <div class="w-full self-form-wrap-item">
                                <label class="e5-form-label" for="personalCode">{{ $t('info.identity_code') }}</label>
                                <div class="relative">
                                    <input type="text" id="personalCode" placeholder="" name="personalCode"
                                           v-model="personalCode" :disabled="this.customerInfo.personal_code!==''"
                                           class="e5-input">
                                </div>
                            </div>

                            <Dob :label="$t('info.dob')" :presetDate="birthDate" @update:date="updateBirthDate" is-dob
                                 :disable="this.customerInfo.personal_code!==''"/>

                            <div class="w-full self-form-wrap-item">
                                <label class="e5-form-label" for="gender">{{ $t('info.gender') }}</label>
                                <div class="relative">
                                    <input type="text" id="gender" placeholder="" name="gender"
                                           v-model="translatedGender"
                                           disabled
                                           class="e5-input">
                                </div>
                            </div>

                            <div class="flex items-center justify-between w-full px-8 pt-16">
                                <div>
                                    <button @click="submitChanges" class="self-btn-primary" data-micromodal-close>
                                        {{ $t('info.save') }}
                                        <span class="text-yellow-300 self-btn-primary__icon">
                                            <IconArrowAltSVG/>
                                        </span>
                                    </button>
                                </div>
                                <div>
                                    <button data-micromodal-close class="self-btn-plain">
                                        <span class="w-16 ml-0 mr-4 self-btn-plain__icon">
                                            <IconXSVG/>
                                        </span>
                                        <span @click="cancelChanges"
                                              class="self-btn-plain__text">{{ $t('info.cancel') }}</span>
                                    </button>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </div>
        <!--    MODAL END-->
    </div>
</template>

<script lang="ts">
import { Component, Vue } from 'vue-property-decorator'
import IconNotesSVG from '@/assets/static/icons-src/icon-notes.svg'
import IconPencilSVG from '@/assets/static/icons-src/icon-pencil.svg'
import IconXSVG from '@/assets/static/icons-src/icon-x.svg'
import IconArrowAltSVG from '@/assets/static/icons-src/icon-arrow-alt.svg'
import { ICustomer } from '@/domain/ICustomer'
import Dob from '@/components/info-view-components/Dob.vue'
import { IBirthdateRequest } from "@/domain/ISaveInfo"
import store from "@/store";

@Component({
    components: {
        IconNotesSVG,
        IconPencilSVG,
        IconXSVG,
        IconArrowAltSVG,
        Dob
    }
})
export default class PersonalData extends Vue {
    personalCode = ''
    birthDate = ''
    gender = ''
    selectedBirthDate = ''

    get customerInfo (): ICustomer {
        return this.$store.getters.getCustomerInfo
    }

    get translatedGender() {
        return this.$t(`info.genders.${this.gender}`);
    }

    created () {
        this.calculateBirthAndGender()
        this.selectedBirthDate = this.birthDate
    }

    private calculateBirthAndGender () {
        this.personalCode = this.customerInfo.personal_code ? this.customerInfo.personal_code : ''
        if (this.personalCode.length === 11) {
            const year = this.personalCode.substring(1, 3)
            const month = parseInt(this.personalCode.substring(3, 5))
            const day = parseInt(this.personalCode.substring(5, 7))

            // Determine the century based on the first digit of the personal code
            let century: string
            switch (this.personalCode.charAt(0)) {
            case '1':
            case '2':
                century = '18'
                break
            case '3':
            case '4':
                century = '19'
                break
            case '5':
            case '6':
                century = '20'
                break
            case '7':
            case '8':
                century = '21'
                break
            default:
                century = 'Invalid'
                return
            }

            // Construct the birthdate
            const birthDate = new Date(parseInt(century + year), month - 1, day)
            this.birthDate = birthDate.toLocaleDateString('et-EE', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            })

            // Determine gender based on the first digit of the personal code
            this.gender = parseInt(this.personalCode.charAt(0)) % 2 === 0 ? 'Female' : 'Male'
        }
    }

    updateBirthDate(newBirthDate: string) {
        this.selectedBirthDate = newBirthDate;
    }

    async submitChanges() {
        const request: IBirthdateRequest = {
            customerId: this.customerInfo.customerid,
            birthDate: this.selectedBirthDate
        }
        await store.dispatch('saveBirthdate', request)
        // Update customerinfo to display new subscriptions
        await store.dispatch("updateCustomerInfo")
    }

    cancelChanges() {
        this.calculateBirthAndGender()
    }
}
</script>

<style scoped>

</style>
